import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/LanguageContext';
import { PublicApi } from '../../utils/apiClient';
import { Phone } from 'lucide-react';
import FadeIn from './FadeIn';

const PRIVACY_LABEL: Record<string, string> = {
  de: 'Datenschutzerklärung', en: 'Privacy Policy', it: 'Privacy Policy',
};

const phoneIcon = <Phone size={13} aria-hidden style={{ display: 'inline', verticalAlign: '-2px', marginRight: 5, color: 'var(--blue)' }} />;

const PLACEHOLDERS: Record<string, string[]> = {
  de: ['Ihr Name', 'Ihr Unternehmen', 'ihre@email.de', 'Ihre Anfrage...'],
  en: ['Your Name', 'Your Company', 'your@email.com', 'Your inquiry...'],
  it: ['Nome', 'Azienda', 'vostra@email.it', 'La vostra richiesta...'],
};

const Contact: React.FC = () => {
  const { t, lang } = useI18n();
  const ph = PLACEHOLDERS[lang] || PLACEHOLDERS.de;
  const [form, setForm] = useState({ name: '', company: '', email: '', message: '' });
  const [ok, setOk] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await PublicApi.contact({ ...form, lang });
    } catch {
      /* Show success regardless — matches original UX; message is best-effort. */
    } finally {
      setSending(false);
      setOk(true);
      setForm({ name: '', company: '', email: '', message: '' });
      setTimeout(() => setOk(false), 5000);
    }
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact">
      <div className="container">
        <FadeIn style={{ marginBottom: '2.5rem' }}>
          <span className="slabel sl-b">{t('con.badge')}</span>
          <h2 className="stitle">{t('con.title')}</h2>
          <p className="ssub">{t('con.sub')}</p>
        </FadeIn>
        <div className="contact-grid">
          <FadeIn variant="left" className="addr-list">
            <div className="addr-card" style={{ borderLeft: '3px solid var(--blue)' }}>
              <div className="addr-type">{t('con.hq')}</div>
              <div className="addr-name">{t('info.hq.name')}</div>
              <div className="addr-text" dangerouslySetInnerHTML={{ __html: t('info.hq.addr') }} />
            </div>
            <div className="addr-card">
              <div className="addr-type">{t('con.north')}</div>
              <div className="addr-name">{t('info.north.name')}</div>
              <div className="addr-text">
                <span dangerouslySetInnerHTML={{ __html: t('info.north.addr') }} /><br />
                {phoneIcon}<a href={`tel:${t('info.north.phoneHref')}`}>{t('info.north.phone')}</a>
              </div>
            </div>
            <div className="addr-card">
              <div className="addr-type">{t('con.south')}</div>
              <div className="addr-name">{t('info.south.name')}</div>
              <div className="addr-text">
                <span dangerouslySetInnerHTML={{ __html: t('info.south.addr') }} /><br />
                {phoneIcon}<a href={`tel:${t('info.south.phoneHref')}`}>{t('info.south.phone')}</a>
              </div>
            </div>
            <div className="addr-card">
              <div className="addr-type">{t('con.dus')}</div>
              <div className="addr-name">{t('info.dus.name')}</div>
              <div className="addr-text">{t('con.dus.text')}</div>
            </div>
            <div className="addr-card">
              <div className="addr-type">Email</div>
              <div className="addr-name"><a href={`mailto:${t('info.email')}`}>{t('info.email')}</a></div>
              <div className="addr-text"><span>{t('con.global')}</span></div>
            </div>
          </FadeIn>

          <FadeIn variant="right" className="cform" style={{ transitionDelay: '.15s' }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>{t('con.form.title')}</h3>
            <form onSubmit={submit}>
              <div className="form-group">
                <label className="form-label" htmlFor="cf-name">{t('con.form.name')}</label>
                <input id="cf-name" name="name" autoComplete="name" type="text" className="form-input" placeholder={ph[0]} value={form.name} onChange={set('name')} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="cf-company">{t('con.form.company')}</label>
                <input id="cf-company" name="organization" autoComplete="organization" type="text" className="form-input" placeholder={ph[1]} value={form.company} onChange={set('company')} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="cf-email">Email</label>
                <input id="cf-email" name="email" autoComplete="email" type="email" className="form-input" placeholder={ph[2]} value={form.email} onChange={set('email')} required />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="cf-message">{t('con.form.msg')}</label>
                <textarea id="cf-message" name="message" className="form-input" placeholder={ph[3]} value={form.message} onChange={set('message')} required />
              </div>
              <button type="submit" className="form-btn" disabled={sending}>{t('con.form.send')}</button>
              <p className="form-privacy">
                {t('con.form.privacy')}{' '}
                <Link to="/privacy-policy">{PRIVACY_LABEL[lang] || PRIVACY_LABEL.en}</Link>.
              </p>
              {ok && <div className="form-ok" style={{ display: 'block' }}>{t('con.form.ok')}</div>}
            </form>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

export default Contact;
